/**
 * TW: Combined listing selector.
 *
 * Design comes from TW: Scent Boxes; the behaviour does not. Each option value of a
 * combined listing belongs to a DIFFERENT product, so choosing one has to move the
 * shopper to that sibling product — new title, price, gallery, description and
 * metafield-driven sections — without a full page reload.
 *
 * Two cases, told apart by comparing the value's product_url with the current one
 * (the approach Shopify documents for combined listings):
 *
 *   same product     -> hand off to TwVariantSelector.selectVariant() (ordinary variant change)
 *   sibling product  -> fetch that product's page, swap the sections, pushState the new URL
 *
 * Why a full section swap rather than TwVariantSelector.refreshSection(): that function
 * patches individual pieces (the .price node, availability attributes) because it assumes
 * the product is unchanged. Here everything below the header belongs to a different
 * product, so each section's markup is replaced wholesale.
 *
 * Why pushState and not replaceState: a sibling product is a genuinely different page, so
 * Back should return to the previous product. Variant changes use replaceState precisely
 * because they are not separate pages.
 */
window.TwCombinedListing = (function () {
  'use strict';

  var BOX_SELECTOR = '.tw-cl-box';
  var ROOT_SELECTOR = '[data-tw-combined-listing]';
  var MAIN_ID = 'MainContent';

  // Guards against out-of-order responses when a shopper clicks through options quickly:
  // only the newest request is allowed to touch the DOM.
  var latestRequest = 0;

  /** Compare by pathname so ?variant=… or a trailing slash never reads as a different product. */
  function pathOf(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (e) {
      return String(url).replace(/\/+$/, '');
    }
  }

  function isSameProduct(url) {
    return pathOf(url) === pathOf(window.location.pathname);
  }

  function setLoading(root, isLoading) {
    if (root) root.setAttribute('data-tw-cl-loading', isLoading ? 'true' : 'false');
  }

  /** Optimistic highlight so the box reacts on click, before the network returns. */
  function markSelected(root, box) {
    if (!root) return;
    root.querySelectorAll(BOX_SELECTOR).forEach(function (b) {
      b.setAttribute('aria-selected', b === box ? 'true' : 'false');
    });
  }

  /**
   * Replace every section present in BOTH documents, matched by section id.
   * Sibling products share a template, so ids line up; anything only in one document
   * (a section the other product doesn't use) is left alone rather than guessed at.
   * Header and footer sit outside #MainContent and are deliberately untouched.
   */
  function swapSections(doc) {
    var main = document.getElementById(MAIN_ID);
    var freshMain = doc.getElementById(MAIN_ID);
    if (!main || !freshMain) return [];

    var swappedIds = [];
    main.querySelectorAll('.shopify-section').forEach(function (current) {
      if (!current.id) return;
      // Attribute selector, not '#' + id: section ids are merchant-controlled and can
      // contain characters that break a CSS id selector.
      var fresh = freshMain.querySelector('.shopify-section[id="' + current.id + '"]');
      if (!fresh) return;
      current.innerHTML = fresh.innerHTML;
      swappedIds.push(current.id.replace(/^shopify-section-/, ''));
    });
    return swappedIds;
  }

  /**
   * Yotpo mounts each widget once, into the placeholders it finds when its loader runs.
   * The section swap throws that mounted DOM away and puts back a bare placeholder for the
   * new product, so without this the reviews section renders empty — which also leaves the
   * star-rating's "scroll to reviews" click with nothing to land on. initWidgets() re-scans
   * the document and mounts whatever placeholders are currently there.
   *
   * Absent loader is not an error: if the swap wins the race against Yotpo's own script,
   * that script finds the fresh placeholders on its own when it does load.
   */
  function reinitReviewWidgets() {
    var container = window.yotpoWidgetsContainer;
    if (!container || typeof container.initWidgets !== 'function') return;

    try {
      var pending = container.initWidgets();
      if (pending && typeof pending.catch === 'function') {
        pending.catch(function (err) {
          console.warn('[TwCombinedListing] Yotpo re-init failed', err);
        });
      }
    } catch (err) {
      console.warn('[TwCombinedListing] Yotpo re-init failed', err);
    }
  }

  /**
   * Re-run the theme's per-section setup. Most PDP scripts delegate from `document`, so
   * they survive the swap on their own; the ones that initialise on load listen for
   * shopify:section:load (the same event the theme editor fires), so reusing it here
   * avoids inventing a second convention.
   */
  function reinitSections(sectionIds) {
    sectionIds.forEach(function (sectionId) {
      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: sectionId },
        })
      );
    });

    if (window.TwIngredientsVariantSync && typeof window.TwIngredientsVariantSync.syncFromPageVariant === 'function') {
      window.TwIngredientsVariantSync.syncFromPageVariant();
    }
    if (window.syncPurchaseTypeDefault) {
      window.syncPurchaseTypeDefault();
    }

    reinitReviewWidgets();
  }

  function updateDocumentTitle(doc) {
    var freshTitle = doc.querySelector('title');
    if (freshTitle && freshTitle.textContent) {
      document.title = freshTitle.textContent;
    }
  }

  /**
   * Move to a sibling product.
   * @param {string} productUrl - the sibling product's URL
   * @param {Object} [opts] - { push: boolean, root: Element }
   */
  function switchToProduct(productUrl, opts) {
    var options = opts || {};
    var push = options.push !== false;
    var root = options.root || document.querySelector(ROOT_SELECTOR);

    if (!productUrl) return Promise.resolve(false);

    var requestId = ++latestRequest;
    setLoading(root, true);

    return fetch(productUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Combined listing fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (requestId !== latestRequest) return false;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var swappedIds = swapSections(doc);

        if (!swappedIds.length) {
          // Nothing matched — the products don't share a template. A real navigation is
          // better than leaving the shopper on stale content.
          window.location.assign(productUrl);
          return false;
        }

        updateDocumentTitle(doc);
        if (push) {
          window.history.pushState({ twCombinedListing: true }, '', productUrl);
        }
        reinitSections(swappedIds);

        document.dispatchEvent(
          new CustomEvent('tw-combined-listing:switched', {
            bubbles: true,
            detail: { productUrl: productUrl, sectionIds: swappedIds },
          })
        );
        return true;
      })
      .catch(function (err) {
        console.warn('[TwCombinedListing]', err);
        // Never strand the shopper on a dead click.
        window.location.assign(productUrl);
        return false;
      })
      .then(function (result) {
        if (requestId === latestRequest) setLoading(root, false);
        return result;
      });
  }

  function onClick(event) {
    var box = event.target.closest(BOX_SELECTOR);
    if (!box || box.disabled) return;

    var root = box.closest(ROOT_SELECTOR);
    if (!root) return;

    var productUrl = box.getAttribute('data-product-url');
    var variantId = box.getAttribute('data-variant-id');

    markSelected(root, box);

    // Same product: this is a plain variant change, so use the theme's canonical flow
    // rather than re-implementing form/URL/price syncing.
    if (!productUrl || isSameProduct(productUrl)) {
      if (variantId && window.TwVariantSelector && typeof window.TwVariantSelector.selectVariant === 'function') {
        window.TwVariantSelector.selectVariant({
          sectionId: root.dataset.sectionId,
          productUrl: root.dataset.productUrl,
          variantId: variantId,
          container: root,
        });
      }
      return;
    }

    switchToProduct(productUrl, { root: root });
  }

  /**
   * Back/forward across sibling products. Because the swap is client-side, the browser has
   * no document to restore, so the target URL is re-applied the same way — without pushing
   * another entry, which would trap the shopper in the history stack.
   */
  function onPopState() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;
    if (isSameProduct(root.dataset.productUrl)) return;
    switchToProduct(window.location.pathname + window.location.search, { push: false, root: root });
  }

  function init() {
    if (window.__twCombinedListingBound) return;
    window.__twCombinedListingBound = true;
    document.addEventListener('click', onClick);
    window.addEventListener('popstate', onPopState);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return { switchToProduct: switchToProduct };
})();
